import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Autocomplete } from "@react-google-maps/api";
import { Icon } from "@iconify/react";
import { useGoogleMaps } from "../context/GoogleMapsContext";
import api from "../services/api";
import { showToast } from "../helper/notifications";

const splitAddressLine = (addressLine = "") => {
  const trimmed = String(addressLine || "").trim();
  if (!trimmed) return { street: "", houseNumber: "" };
  const match = trimmed.match(/^(.*?)\s+(\d+[a-zA-Z0-9\-\/]*)$/);
  if (match) {
    return { street: match[1].trim(), houseNumber: match[2].trim() };
  }
  return { street: trimmed, houseNumber: "" };
};

const CreateOrLinkMasterLocationModal = ({
  isOpen,
  onClose,
  gbpLocation = null,
  gbpLocationsList = [],
  onSuccess,
}) => {
  const navigate = useNavigate();
  const { isLoaded } = useGoogleMaps();
  const autocompleteRef = useRef(null);

  const [selectedGbpId, setSelectedGbpId] = useState("");
  const [allGbpLocations, setAllGbpLocations] = useState([]);
  const [masterLocations, setMasterLocations] = useState([]);
  const [topLocations, setTopLocations] = useState([]);
  const [loadingLookups, setLoadingLookups] = useState(false);

  const [googleSearchText, setGoogleSearchText] = useState("");
  const [selectedGooglePlace, setSelectedGooglePlace] = useState(null);

  // Mode: 'create' (Create New in Master Data & Link) or 'link' (Link to Existing Master Location)
  const [actionMode, setActionMode] = useState("create");
  const [selectedMasterLocationId, setSelectedMasterLocationId] = useState("");
  const [masterSearchFilter, setMasterSearchFilter] = useState("");

  const [formData, setFormData] = useState({
    location_name: "",
    store_code: "",
    top_location_id: "",
    street_address: "",
    house_number: "",
    postal_code: "",
    city: "",
    country: "AT",
    lat: "",
    lng: "",
    phone_number: "",
    website_url: "",
    primary_category_id: "",
    description: "",
    google_place_id: "",
    google_link: "",
    internal_id: "",
  });

  // Store code validation state
  const [storeCodeStatus, setStoreCodeStatus] = useState({
    checking: false,
    exists: false,
    conflictingLocation: null,
    suggestion: null,
  });

  const [submitting, setSubmitting] = useState(false);

  // Determine active GBP record
  const activeGbp = React.useMemo(() => {
    if (gbpLocation && (!selectedGbpId || String(gbpLocation.id) === String(selectedGbpId))) {
      return gbpLocation;
    }
    const list = allGbpLocations.length > 0 ? allGbpLocations : gbpLocationsList;
    return list.find((item) => String(item.id) === String(selectedGbpId)) || null;
  }, [gbpLocation, selectedGbpId, allGbpLocations, gbpLocationsList]);

  // Populate form from a GBP record
  const populateFromGbp = (gbp) => {
    if (!gbp) {
      setFormData({
        location_name: "",
        store_code: "",
        top_location_id: "",
        street_address: "",
        house_number: "",
        postal_code: "",
        city: "",
        country: "AT",
        lat: "",
        lng: "",
        phone_number: "",
        website_url: "",
        primary_category_id: "",
        description: "",
        google_place_id: "",
        google_link: "",
        internal_id: "",
      });
      setGoogleSearchText("");
      setSelectedGooglePlace(null);
      return;
    }

    const { street, houseNumber } = splitAddressLine(gbp.address_line1 || "");
    const cityVal = gbp.locality || gbp.city || "";
    const queryParts = [gbp.title, gbp.address_line1, gbp.postal_code, cityVal].filter(Boolean);

    setGoogleSearchText(queryParts.join(", "));
    setSelectedGooglePlace(null);

    setFormData({
      location_name: gbp.title || "",
      store_code: gbp.store_code || "",
      top_location_id: "",
      street_address: street,
      house_number: houseNumber,
      postal_code: gbp.postal_code || "",
      city: cityVal,
      country: gbp.country_code || "AT",
      lat: gbp.latitude ?? "",
      lng: gbp.longitude ?? "",
      phone_number: gbp.primary_phone || "",
      website_url: gbp.website_uri || "",
      primary_category_id: gbp.primary_category_name || "",
      description: gbp.description || "",
      google_place_id: gbp.google_place_id || "",
      google_link: gbp.maps_uri || "",
      internal_id: gbp.google_location_id || "",
    });

    if (gbp.linked_location_id) {
      setSelectedMasterLocationId(String(gbp.linked_location_id));
    } else {
      setSelectedMasterLocationId("");
    }
  };

  // Load lookups when modal opens
  useEffect(() => {
    if (!isOpen) return;

    const initialGbpId = gbpLocation?.id ? String(gbpLocation.id) : "";
    setSelectedGbpId(initialGbpId);
    setActionMode("create");
    populateFromGbp(gbpLocation);

    const fetchLookups = async () => {
      setLoadingLookups(true);
      try {
        const [masterRes, topRes, gbpRes] = await Promise.all([
          api.get("/main-locations", { params: { limit: 500, page: 1 } }),
          api.get("/top-locations", { params: { limit: 200 } }).catch(() => ({ data: { data: [] } })),
          !gbpLocation
            ? api.get("/google-business/locations", { params: { limit: 250, page: 1 } }).catch(() => ({ data: { data: [] } }))
            : Promise.resolve({ data: { data: [] } }),
        ]);

        const masters = masterRes.data?.data || [];
        setMasterLocations(masters);
        setTopLocations(topRes.data?.data || []);
        if (!gbpLocation && gbpRes.data?.data) {
          setAllGbpLocations(gbpRes.data.data);
        }
      } catch (err) {
        console.error("Failed to load modal lookups:", err);
      } finally {
        setLoadingLookups(false);
      }
    };

    fetchLookups();
  }, [isOpen, gbpLocation]);

  // Auto-detect matching Master Location when form or masterLocations change
  const matchedMasterLocation = React.useMemo(() => {
    if (!masterLocations.length) return null;
    if (activeGbp?.linked_location_id) {
      const byId = masterLocations.find(
        (m) => String(m.location_id) === String(activeGbp.linked_location_id)
      );
      if (byId) return byId;
    }
    if (formData.google_place_id) {
      const byPlace = masterLocations.find(
        (m) => m.google_place_id && m.google_place_id === formData.google_place_id
      );
      if (byPlace) return byPlace;
    }
    if (formData.store_code) {
      const byCode = masterLocations.find(
        (m) =>
          m.store_code &&
          m.store_code.trim().toLowerCase() === formData.store_code.trim().toLowerCase()
      );
      if (byCode) return byCode;
    }
    if (formData.location_name) {
      const byName = masterLocations.find(
        (m) =>
          m.location_name &&
          m.location_name.trim().toLowerCase() === formData.location_name.trim().toLowerCase()
      );
      if (byName) return byName;
    }
    return null;
  }, [masterLocations, activeGbp, formData.google_place_id, formData.store_code, formData.location_name]);

  // Live store_code duplicate check
  useEffect(() => {
    if (!isOpen || actionMode !== "create") return;
    const trimmed = String(formData.store_code || "").trim();
    if (!trimmed) {
      setStoreCodeStatus({
        checking: false,
        exists: false,
        conflictingLocation: null,
        suggestion: null,
      });
      return;
    }

    const timer = setTimeout(async () => {
      setStoreCodeStatus((prev) => ({ ...prev, checking: true }));
      try {
        const res = await api.get("/main-locations/check-store-code", {
          params: { store_code: trimmed },
        });
        if (res.data?.success) {
          setStoreCodeStatus({
            checking: false,
            exists: Boolean(res.data.exists),
            conflictingLocation: res.data.conflictingLocation || null,
            suggestion: res.data.suggestion || null,
          });
        }
      } catch {
        setStoreCodeStatus((prev) => ({ ...prev, checking: false }));
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [formData.store_code, isOpen, actionMode]);

  const handleGenerateStoreCode = async () => {
    try {
      const res = await api.get("/main-locations/generate-store-code", {
        params: { prefix: "STR-" },
      });
      if (res.data?.store_code) {
        setFormData((prev) => ({ ...prev, store_code: res.data.store_code }));
      }
    } catch {
      showToast("error", "Could not generate store code");
    }
  };

  // Google Places Autocomplete handlers
  const onLoadAutocomplete = (autocompleteInstance) => {
    autocompleteRef.current = autocompleteInstance;
  };

  const onPlaceChanged = () => {
    if (!autocompleteRef.current) return;
    const place = autocompleteRef.current.getPlace();
    if (!place || (!place.geometry && !place.place_id && !place.name)) return;

    let street = "";
    let streetNumber = "";
    let postalCode = "";
    let city = "";
    let country = "";

    if (Array.isArray(place.address_components)) {
      place.address_components.forEach((comp) => {
        const types = comp.types || [];
        if (types.includes("route")) street = comp.long_name;
        if (types.includes("street_number")) streetNumber = comp.long_name;
        if (types.includes("postal_code")) postalCode = comp.long_name;
        if (types.includes("locality") || types.includes("postal_town")) {
          if (!city) city = comp.long_name;
        }
        if (types.includes("country")) country = comp.short_name;
      });
    }

    const lat = place.geometry?.location ? place.geometry.location.lat() : "";
    const lng = place.geometry?.location ? place.geometry.location.lng() : "";

    const primaryType =
      Array.isArray(place.types) && place.types.length > 0
        ? place.types
            .find((t) => !["point_of_interest", "establishment"].includes(t))
            ?.replace(/_/g, " ") || place.types[0].replace(/_/g, " ")
        : "";

    setSelectedGooglePlace({
      name: place.name || "",
      formatted_address: place.formatted_address || "",
      place_id: place.place_id || "",
      url: place.url || "",
    });

    if (place.formatted_address || place.name) {
      setGoogleSearchText(
        [place.name, place.formatted_address].filter(Boolean).join(" — ")
      );
    }

    setFormData((prev) => ({
      ...prev,
      location_name: place.name || prev.location_name,
      street_address: street || prev.street_address,
      house_number: streetNumber || prev.house_number,
      postal_code: postalCode || prev.postal_code,
      city: city || prev.city,
      country: country || prev.country || "AT",
      lat: lat !== "" ? Number(lat.toFixed(6)) : prev.lat,
      lng: lng !== "" ? Number(lng.toFixed(6)) : prev.lng,
      phone_number:
        place.international_phone_number ||
        place.formatted_phone_number ||
        prev.phone_number,
      website_url: place.website || prev.website_url,
      google_place_id: place.place_id || prev.google_place_id,
      google_link: place.url || prev.google_link,
      primary_category_id: prev.primary_category_id || primaryType,
    }));

    showToast("info", `Selected "${place.name || "Location"}" from Google Places!`);
  };

  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (openAfterCreate = false) => {
    if (actionMode === "create") {
      if (!String(formData.location_name || "").trim()) {
        showToast("error", "Location Name is required.");
        return;
      }
      if (storeCodeStatus.exists) {
        showToast(
          "error",
          `Store Code "${formData.store_code}" already exists. Please use the suggested code or enter a unique one.`
        );
        return;
      }
    } else {
      const targetId = selectedMasterLocationId || matchedMasterLocation?.location_id;
      if (!targetId) {
        showToast("error", "Please select an existing Master Data location to link.");
        return;
      }
    }

    setSubmitting(true);
    try {
      const gbpId = activeGbp?.id || null;
      const endpoint = gbpId
        ? `/google-business/locations/${gbpId}/create-or-link-master`
        : `/google-business/create-or-link-master`;

      const payload =
        actionMode === "link"
          ? {
              mode: "link",
              gbp_id: gbpId,
              target_location_id:
                selectedMasterLocationId || matchedMasterLocation?.location_id,
              google_place_id: formData.google_place_id,
              google_link: formData.google_link,
              store_code: formData.store_code,
              internal_id: formData.internal_id,
            }
          : {
              mode: "create",
              gbp_id: gbpId,
              ...formData,
            };

      const res = await api.post(endpoint, payload);

      if (res.data?.success) {
        const createdOrLinkedId = res.data.data?.location_id;
        showToast(
          "success",
          res.data.message ||
            (actionMode === "create"
              ? "New location created in Master Data and linked!"
              : "Location linked to Master Data!")
        );
        if (onSuccess) onSuccess(res.data.data);
        onClose();
        if (openAfterCreate && createdOrLinkedId) {
          navigate(`/locations-main/${createdOrLinkedId}/edit`);
        }
      }
    } catch (err) {
      const resData = err.response?.data;
      if (err.response?.status === 409 && resData?.code === "DUPLICATE_STORE_CODE") {
        setStoreCodeStatus({
          checking: false,
          exists: true,
          conflictingLocation: resData.conflictingLocation || null,
          suggestion: resData.suggestion || null,
        });
        showToast("error", resData.message || "Store code already exists.");
      } else {
        showToast("error", resData?.error || resData?.message || "Operation failed.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const filteredMasterList = masterLocations.filter((loc) => {
    if (!masterSearchFilter.trim()) return true;
    const q = masterSearchFilter.toLowerCase();
    return (
      (loc.location_name || "").toLowerCase().includes(q) ||
      (loc.store_code || "").toLowerCase().includes(q) ||
      (loc.city || "").toLowerCase().includes(q) ||
      (loc.street_address || "").toLowerCase().includes(q)
    );
  });

  const gbpSelectorList = allGbpLocations.length > 0 ? allGbpLocations : gbpLocationsList;

  return (
    <>
      {/* Ensure Google Places Autocomplete dropdown appears above Bootstrap modal */}
      <style>{`
        .pac-container {
          z-index: 9999 !important;
          border-radius: 10px !important;
          box-shadow: 0 12px 28px rgba(15, 23, 42, 0.18) !important;
          border: 1px solid #e2e8f0 !important;
          margin-top: 4px !important;
          font-family: inherit !important;
        }
        .pac-item {
          padding: 8px 14px !important;
          cursor: pointer !important;
          font-size: 13px !important;
        }
        .pac-item:hover {
          background-color: #f1f5f9 !important;
        }
      `}</style>

      <div
        className="modal fade show d-block"
        style={{ backgroundColor: "rgba(15, 23, 42, 0.6)", zIndex: 1055 }}
        tabIndex="-1"
        onClick={onClose}
      >
        <div
          className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-content radius-16 border-0 shadow-lg overflow-hidden">
            {/* Header */}
            <div className="modal-header bg-neutral-50 border-bottom px-24 py-16 d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center gap-12">
                <div className="w-40-px h-40-px rounded-circle bg-primary-50 text-primary-600 d-flex align-items-center justify-content-center flex-shrink-0">
                  <Icon icon="solar:map-point-add-bold-duotone" className="text-2xl" />
                </div>
                <div>
                  <h5 className="modal-title fw-bold text-md mb-2">
                    Create or Link Location in Master Data
                  </h5>
                  <p className="text-xs text-secondary-light mb-0">
                    Search via Google Autocomplete, create a new Master Data record, and link it to Google Business Profile.
                  </p>
                </div>
              </div>
              <button type="button" className="btn-close" onClick={onClose} />
            </div>

            {/* Body */}
            <div className="modal-body p-24">
              {/* Step 1: GBP Source Location Info or Selector */}
              {gbpLocation ? (
                <div className="p-16 radius-12 bg-neutral-50 border border-neutral-200 mb-20">
                  <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                    <div className="d-flex align-items-center gap-10">
                      <Icon icon="logos:google-icon" className="text-xl flex-shrink-0" />
                      <div>
                        <div className="d-flex align-items-center gap-2">
                          <span className="fw-bold text-sm text-primary-light">
                            {gbpLocation.title || "Unnamed GBP Profile"}
                          </span>
                          {gbpLocation.store_code && (
                            <span className="badge bg-neutral-200 text-neutral-700 text-xs px-8 py-2 radius-4">
                              Store Code: {gbpLocation.store_code}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-secondary-light mt-2">
                          {[gbpLocation.address_line1, gbpLocation.postal_code, gbpLocation.locality || gbpLocation.city]
                            .filter(Boolean)
                            .join(", ") || "No address in GBP profile"}
                        </div>
                      </div>
                    </div>

                    {gbpLocation.linked_location_id ? (
                      <span className="badge bg-success-focus text-success-main px-10 py-4 radius-6 text-xs d-inline-flex align-items-center gap-1">
                        <Icon icon="solar:link-circle-bold" className="text-sm" />
                        Linked to: {gbpLocation.linked_location_name} (#{gbpLocation.linked_location_id})
                      </span>
                    ) : (
                      <span className="badge bg-warning-focus text-warning-main px-10 py-4 radius-6 text-xs d-inline-flex align-items-center gap-1">
                        <Icon icon="solar:link-broken-linear" className="text-sm" />
                        Not Linked in Master Data
                      </span>
                    )}
                  </div>
                </div>
              ) : (
                <div className="mb-20">
                  <label className="form-label text-xs fw-bold text-secondary-light text-uppercase mb-6 d-flex align-items-center gap-1">
                    <Icon icon="logos:google-icon" className="text-sm" />
                    Link with Google Business Profile Location (Optional)
                  </label>
                  <select
                    className="form-select radius-8 text-sm"
                    value={selectedGbpId}
                    onChange={(e) => {
                      const val = e.target.value;
                      setSelectedGbpId(val);
                      const found = gbpSelectorList.find((item) => String(item.id) === String(val));
                      populateFromGbp(found || null);
                    }}
                  >
                    <option value="">
                      — Create standalone Master Location (or select a GBP Profile to link) —
                    </option>
                    {gbpSelectorList.map((g) => (
                      <option key={g.id} value={g.id}>
                        {g.linked_location_id ? "[Linked] " : "[Unlinked] "}
                        {g.title || "Unnamed"} {g.store_code ? `(${g.store_code})` : ""} —{" "}
                        {g.locality || g.city || "No city"}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Step 2: Google Places Autocomplete Search */}
              <div className="p-16 radius-12 border border-primary-200 bg-primary-50/40 mb-20" style={{ backgroundColor: "#f8faff" }}>
                <label className="form-label text-xs fw-bold text-primary-600 text-uppercase mb-6 d-flex align-items-center justify-content-between">
                  <span className="d-inline-flex align-items-center gap-6">
                    <Icon icon="solar:magnifer-bold-duotone" className="text-base" />
                    Search & Select Location on Google Places (Autocomplete)
                  </span>
                  {selectedGooglePlace?.place_id && (
                    <span className="badge bg-success-focus text-success-main radius-4 px-8 py-2 text-xs fw-medium">
                      Place ID: {selectedGooglePlace.place_id.slice(0, 14)}...
                    </span>
                  )}
                </label>

                {isLoaded ? (
                  <Autocomplete onLoad={onLoadAutocomplete} onPlaceChanged={onPlaceChanged}>
                    <div className="position-relative">
                      <input
                        type="text"
                        className="form-control radius-8 ps-36 text-sm border-primary-300"
                        placeholder="Type business name or address to search on Google Maps..."
                        value={googleSearchText}
                        onChange={(e) => setGoogleSearchText(e.target.value)}
                      />
                      <Icon
                        icon="logos:google-maps"
                        className="position-absolute top-50 translate-middle-y ms-12 text-base"
                        style={{ left: 0, pointerEvents: "none" }}
                      />
                      {googleSearchText && (
                        <button
                          type="button"
                          className="btn btn-sm position-absolute top-50 translate-middle-y me-4 p-4 text-secondary-light border-0"
                          style={{ right: 0 }}
                          onClick={() => {
                            setGoogleSearchText("");
                            setSelectedGooglePlace(null);
                          }}
                          title="Clear search"
                        >
                          <Icon icon="radix-icons:cross-2" />
                        </button>
                      )}
                    </div>
                  </Autocomplete>
                ) : (
                  <input
                    type="text"
                    className="form-control radius-8 text-sm"
                    placeholder="Loading Google Places Autocomplete..."
                    disabled
                  />
                )}
                <div className="text-xs text-secondary-light mt-6">
                  Selecting a location from the Google dropdown automatically fills the address, coordinates, phone, website, and Place ID below.
                </div>
              </div>

              {/* Matching Master Location Banner (if detected) */}
              {matchedMasterLocation && (
                <div className="alert alert-info radius-10 p-12 mb-20 d-flex align-items-center justify-content-between flex-wrap gap-2 border border-info-200">
                  <div className="d-flex align-items-center gap-10">
                    <Icon icon="solar:info-circle-bold" className="text-info-main text-xl flex-shrink-0" />
                    <div className="text-xs">
                      <strong>Matching location found in Master Data:</strong>{" "}
                      <span className="fw-semibold">{matchedMasterLocation.location_name}</span>{" "}
                      ({matchedMasterLocation.store_code || "No store code"} — {matchedMasterLocation.city || "No city"})
                    </div>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <button
                      type="button"
                      className={`btn btn-xs radius-6 px-10 py-4 text-xs fw-semibold ${
                        actionMode === "link" ? "btn-primary" : "btn-outline-primary"
                      }`}
                      onClick={() => {
                        setActionMode("link");
                        setSelectedMasterLocationId(String(matchedMasterLocation.location_id));
                      }}
                    >
                      Link to Existing
                    </button>
                    <button
                      type="button"
                      className={`btn btn-xs radius-6 px-10 py-4 text-xs fw-semibold ${
                        actionMode === "create" ? "btn-primary" : "btn-outline-secondary"
                      }`}
                      onClick={() => setActionMode("create")}
                    >
                      Create New Instead
                    </button>
                  </div>
                </div>
              )}

              {/* Mode Switch Tabs: Create New vs Link Existing */}
              <div className="d-flex gap-2 mb-20 border-bottom pb-12">
                <button
                  type="button"
                  className={`btn btn-sm radius-8 px-16 py-8 d-inline-flex align-items-center gap-2 fw-semibold ${
                    actionMode === "create"
                      ? "btn-primary-600 text-white shadow-sm"
                      : "btn-light text-secondary-light"
                  }`}
                  onClick={() => setActionMode("create")}
                >
                  <Icon icon="solar:add-circle-bold" className="text-base" />
                  Create New in Master Data
                </button>
                <button
                  type="button"
                  className={`btn btn-sm radius-8 px-16 py-8 d-inline-flex align-items-center gap-2 fw-semibold ${
                    actionMode === "link"
                      ? "btn-primary-600 text-white shadow-sm"
                      : "btn-light text-secondary-light"
                  }`}
                  onClick={() => {
                    setActionMode("link");
                    if (!selectedMasterLocationId && matchedMasterLocation) {
                      setSelectedMasterLocationId(String(matchedMasterLocation.location_id));
                    }
                  }}
                >
                  <Icon icon="solar:link-circle-bold" className="text-base" />
                  Link to Existing Master Location
                </button>
              </div>

              {actionMode === "create" ? (
                /* CREATE NEW LOCATION FORM */
                <div className="row g-3">
                  {/* Location Name */}
                  <div className="col-md-6">
                    <label className="form-label text-xs fw-bold text-secondary-light mb-4">
                      LOCATION NAME <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control radius-8 text-sm"
                      value={formData.location_name}
                      onChange={(e) => handleFieldChange("location_name", e.target.value)}
                      placeholder="e.g. LoQation Store Salzburg"
                      required
                    />
                  </div>

                  {/* Store Code with live uniqueness check & suggestion */}
                  <div className="col-md-6">
                    <div className="d-flex align-items-center justify-content-between mb-4">
                      <label className="form-label text-xs fw-bold text-secondary-light mb-0">
                        STORE CODE
                      </label>
                      <button
                        type="button"
                        className="btn btn-link p-0 text-xs text-primary-600 text-decoration-none fw-semibold"
                        onClick={handleGenerateStoreCode}
                      >
                        Auto-Generate
                      </button>
                    </div>
                    <input
                      type="text"
                      className={`form-control radius-8 text-sm ${
                        storeCodeStatus.exists ? "border-danger bg-danger-50" : ""
                      }`}
                      value={formData.store_code}
                      onChange={(e) => handleFieldChange("store_code", e.target.value)}
                      placeholder="Leave blank to auto-generate (e.g. STR-001)"
                    />
                    {storeCodeStatus.exists && (
                      <div className="mt-6 p-8 radius-8 bg-danger-focus border border-danger-main text-xs text-danger-main d-flex align-items-center justify-content-between gap-2 flex-wrap">
                        <span>
                          Store code <strong>"{formData.store_code}"</strong> is already used by{" "}
                          <strong>{storeCodeStatus.conflictingLocation?.location_name || "another location"}</strong>.
                        </span>
                        {storeCodeStatus.suggestion && (
                          <button
                            type="button"
                            className="btn btn-xs btn-danger radius-6 px-8 py-2 text-xs fw-semibold"
                            onClick={() => handleFieldChange("store_code", storeCodeStatus.suggestion)}
                          >
                            Use {storeCodeStatus.suggestion}
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Street Address & House Number */}
                  <div className="col-md-8">
                    <label className="form-label text-xs fw-bold text-secondary-light mb-4">
                      STREET ADDRESS
                    </label>
                    <input
                      type="text"
                      className="form-control radius-8 text-sm"
                      value={formData.street_address}
                      onChange={(e) => handleFieldChange("street_address", e.target.value)}
                      placeholder="Street name"
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label text-xs fw-bold text-secondary-light mb-4">
                      HOUSE NO.
                    </label>
                    <input
                      type="text"
                      className="form-control radius-8 text-sm"
                      value={formData.house_number}
                      onChange={(e) => handleFieldChange("house_number", e.target.value)}
                      placeholder="e.g. 12A"
                    />
                  </div>

                  {/* Postal Code, City, Country */}
                  <div className="col-md-4">
                    <label className="form-label text-xs fw-bold text-secondary-light mb-4">
                      POSTAL CODE
                    </label>
                    <input
                      type="text"
                      className="form-control radius-8 text-sm"
                      value={formData.postal_code}
                      onChange={(e) => handleFieldChange("postal_code", e.target.value)}
                      placeholder="e.g. 5020"
                    />
                  </div>
                  <div className="col-md-5">
                    <label className="form-label text-xs fw-bold text-secondary-light mb-4">
                      CITY
                    </label>
                    <input
                      type="text"
                      className="form-control radius-8 text-sm"
                      value={formData.city}
                      onChange={(e) => handleFieldChange("city", e.target.value)}
                      placeholder="e.g. Salzburg"
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label text-xs fw-bold text-secondary-light mb-4">
                      COUNTRY
                    </label>
                    <input
                      type="text"
                      className="form-control radius-8 text-sm"
                      value={formData.country}
                      onChange={(e) => handleFieldChange("country", e.target.value)}
                      placeholder="AT"
                    />
                  </div>

                  {/* Lat, Lng, Top Location */}
                  <div className="col-md-4">
                    <label className="form-label text-xs fw-bold text-secondary-light mb-4">
                      LATITUDE
                    </label>
                    <input
                      type="number"
                      step="any"
                      className="form-control radius-8 text-sm"
                      value={formData.lat}
                      onChange={(e) => handleFieldChange("lat", e.target.value)}
                      placeholder="47.8095"
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label text-xs fw-bold text-secondary-light mb-4">
                      LONGITUDE
                    </label>
                    <input
                      type="number"
                      step="any"
                      className="form-control radius-8 text-sm"
                      value={formData.lng}
                      onChange={(e) => handleFieldChange("lng", e.target.value)}
                      placeholder="13.0550"
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label text-xs fw-bold text-secondary-light mb-4">
                      TOP LOCATION / AREA
                    </label>
                    <select
                      className="form-select radius-8 text-sm"
                      value={formData.top_location_id}
                      onChange={(e) => handleFieldChange("top_location_id", e.target.value)}
                    >
                      <option value="">— None —</option>
                      {topLocations.map((tl) => (
                        <option key={tl.id || tl.top_location_id} value={tl.id || tl.top_location_id}>
                          {tl.name || tl.top_location_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Phone, Website, Primary Category */}
                  <div className="col-md-4">
                    <label className="form-label text-xs fw-bold text-secondary-light mb-4">
                      PHONE NUMBER
                    </label>
                    <input
                      type="text"
                      className="form-control radius-8 text-sm"
                      value={formData.phone_number}
                      onChange={(e) => handleFieldChange("phone_number", e.target.value)}
                      placeholder="+43 ..."
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label text-xs fw-bold text-secondary-light mb-4">
                      WEBSITE URL
                    </label>
                    <input
                      type="text"
                      className="form-control radius-8 text-sm"
                      value={formData.website_url}
                      onChange={(e) => handleFieldChange("website_url", e.target.value)}
                      placeholder="https://..."
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label text-xs fw-bold text-secondary-light mb-4">
                      CATEGORY
                    </label>
                    <input
                      type="text"
                      className="form-control radius-8 text-sm"
                      value={formData.primary_category_id}
                      onChange={(e) => handleFieldChange("primary_category_id", e.target.value)}
                      placeholder="e.g. Shopping Mall"
                    />
                  </div>
                </div>
              ) : (
                /* LINK TO EXISTING MASTER LOCATION */
                <div>
                  <div className="mb-12">
                    <label className="form-label text-xs fw-bold text-secondary-light mb-6">
                      SELECT EXISTING MASTER DATA LOCATION TO LINK
                    </label>
                    <input
                      type="text"
                      className="form-control radius-8 text-sm mb-8"
                      placeholder="Filter existing Master Data locations by name, store code, or city..."
                      value={masterSearchFilter}
                      onChange={(e) => setMasterSearchFilter(e.target.value)}
                    />
                    <div
                      className="border radius-8 overflow-auto"
                      style={{ maxHeight: "240px" }}
                    >
                      {loadingLookups ? (
                        <div className="p-16 text-center text-xs text-secondary-light">
                          Loading Master Data locations...
                        </div>
                      ) : filteredMasterList.length === 0 ? (
                        <div className="p-16 text-center text-xs text-secondary-light">
                          No matching Master Data locations found. Switch to "Create New in Master Data" above.
                        </div>
                      ) : (
                        filteredMasterList.map((loc) => {
                          const isSelected =
                            String(selectedMasterLocationId) === String(loc.location_id);
                          return (
                            <div
                              key={loc.location_id}
                              onClick={() => setSelectedMasterLocationId(String(loc.location_id))}
                              className={`p-12 border-bottom pointer d-flex align-items-center justify-content-between transition-all ${
                                isSelected ? "bg-primary-50 border-primary-300" : "hover-bg-neutral-50"
                              }`}
                            >
                              <div>
                                <div className="d-flex align-items-center gap-2">
                                  <span className="fw-bold text-sm text-primary-light">
                                    {loc.location_name}
                                  </span>
                                  {loc.store_code && (
                                    <span className="badge bg-neutral-200 text-neutral-700 text-xs px-6 py-2 radius-4">
                                      {loc.store_code}
                                    </span>
                                  )}
                                </div>
                                <div className="text-xs text-secondary-light mt-2">
                                  {[
                                    loc.street_address,
                                    loc.house_number,
                                    loc.postal_code,
                                    loc.city,
                                  ]
                                    .filter(Boolean)
                                    .join(" ") || "No address"}
                                </div>
                              </div>
                              <div>
                                {isSelected ? (
                                  <Icon
                                    icon="solar:check-circle-bold"
                                    className="text-primary-600 text-xl"
                                  />
                                ) : (
                                  <span className="text-xs text-secondary-light">Select</span>
                                )}
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="modal-footer bg-neutral-50 border-top px-24 py-14 d-flex align-items-center justify-content-between">
              <button
                type="button"
                className="btn btn-neutral-200 text-neutral-700 radius-8 px-16 py-8 text-sm fw-medium"
                onClick={onClose}
                disabled={submitting}
              >
                Cancel
              </button>

              <div className="d-flex align-items-center gap-2">
                {actionMode === "create" ? (
                  <>
                    <button
                      type="button"
                      className="btn btn-outline-primary radius-8 px-16 py-8 text-sm fw-semibold d-inline-flex align-items-center gap-2"
                      onClick={() => handleSubmit(true)}
                      disabled={submitting || storeCodeStatus.exists}
                    >
                      <Icon icon="lucide:external-link" className="text-base" />
                      <span>Create New & Edit Details</span>
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary-600 radius-8 px-20 py-8 text-sm fw-semibold d-inline-flex align-items-center gap-2 shadow-sm"
                      onClick={() => handleSubmit(false)}
                      disabled={submitting || storeCodeStatus.exists}
                    >
                      <Icon icon="solar:add-circle-bold" className="text-base" />
                      <span>
                        {submitting
                          ? "Creating..."
                          : activeGbp
                          ? "Create New & Link"
                          : "Create New Location"}
                      </span>
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className="btn btn-primary-600 radius-8 px-20 py-8 text-sm fw-semibold d-inline-flex align-items-center gap-2 shadow-sm"
                    onClick={() => handleSubmit(false)}
                    disabled={submitting || !selectedMasterLocationId}
                  >
                    <Icon icon="solar:link-circle-bold" className="text-base" />
                    <span>{submitting ? "Linking..." : "Link Selected Location"}</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CreateOrLinkMasterLocationModal;

